import { ChangeDetectorRef, inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { FlowConstants, TranslationUtils } from '../utils';
import { FlexiExpressionData } from '@customer-apps/flexi-logic';
import { ContextModelService } from '../services';
import { DecimalMarker } from '../../../lib';

@Pipe({
    name: 'translationKey',
    pure: false
})
export class TranslationKeyPipe implements PipeTransform {
    private translate = inject(TranslateService);
    private contextModel = inject(ContextModelService);
    private cdr = inject(ChangeDetectorRef);
    private localeId = inject(LOCALE_ID);

    constructor() {
        this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe(() => this.cdr.markForCheck());
    }

    transform(
        translationKey: string | undefined,
        translationData?: FlexiExpressionData,
        value?: string | number | boolean
    ): string | undefined {
        if (!translationKey) {
            return undefined;
        }

        if (typeof value === 'string' || typeof value === 'number') {
            translationKey = translationKey.replace(FlowConstants.VALUE_PLACEHOLDER, `${value}`);
        }

        const result = this.translate.instant(translationKey);
        const isKeyMissing = result === translationKey;
        if (isKeyMissing) {
            console.warn('Missing translation key', translationKey);
            return undefined;
        }

        // TODO: Handle boolean value (example is needed)

        if (!translationData) {
            return this.translate.instant(translationKey);
        }

        const params = TranslationUtils.sanitizeTranslationParams(this.contextModel.processJsonLogic(translationData));
        const localizedParams = TranslationUtils.localizeNumericParams(params, this.getDecimalSeparator());

        return this.translate.instant(translationKey, localizedParams);
    }

    private getDecimalSeparator(): string {
        return (
            new Intl.NumberFormat(this.localeId.toString()).formatToParts(1.1).find(part => part.type === 'decimal')?.value ??
            DecimalMarker.Dot
        );
    }
}
